import { useQuery } from '@tanstack/react-query';
import {
  brokerageFirmControllerListDocumentsQueryOptions,
  brokerageFirmDocumentExtractionControllerGetBrokerageFirmExtractionsQueryOptions,
  configControllerGetPublicProductConfigQueryOptions,
  configControllerGetPublicTenantConfigQueryOptions,
  policyControllerGetPolicyQueryOptions,
  policyControllerListPolicyDocumentsQueryOptions,
  policyDocumentExtractionControllerGetPolicyExtractionsQueryOptions,
} from 'korint-sdk';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';

export type AutoFillEntityType = 'customer' | 'asset' | 'policy' | 'brokerage_firm';

export type AutoFillFieldData = {
  extractedValue: string;
  /** Source field on the extraction document — `configField.extractionFieldKey ?? configField.key`. */
  extractionFieldKey: string;
  documentId: string;
  category?: string;
  filename?: string;
  /** Product the prefill belongs to (undefined for brokerage-firm flow). */
  productId?: string;
  /** Tenant the prefill belongs to. */
  tenantName?: string;
  /** Policy aggregate the prefill belongs to (policy flow only). */
  policyId?: string;
  /** Event-sourcing branch the policy aggregate is on (policy flow only). */
  branchId?: string;
  /** Brokerage firm the prefill belongs to (brokerage-firm flow only). */
  brokerageFirmId?: string;
  /** Entity the field lives on, used to scope keep-rate analytics. */
  entityType: AutoFillEntityType;
};

type ConfigField = {
  key: string;
  extractionSources?: string[];
  extractionFieldKey?: string;
};

type ExtractionEntry = {
  status: string;
  fields?: unknown;
  documentId: string;
  metadata?: { category?: string } | null;
};

function findExtractedCandidates(
  configFields: ConfigField[],
  extractions: ExtractionEntry[],
  fieldKey: string,
  entityType: AutoFillEntityType,
  idToFilename?: Map<string, string>,
  productId?: string,
  tenantName?: string,
  policyId?: string,
  branchId?: string,
  brokerageFirmId?: string,
): AutoFillFieldData[] {
  const configField = configFields.find(f => f.key === fieldKey);
  if (!configField?.extractionSources?.length) return [];

  const extractionKey = configField.extractionFieldKey ?? configField.key;
  const allowedSources = new Set(configField.extractionSources);

  const candidates: AutoFillFieldData[] = [];
  for (const extraction of extractions) {
    if (extraction.status !== 'DONE' || !extraction.fields) continue;
    const category = extraction.metadata?.category;
    if (!category || !allowedSources.has(category)) continue;
    const fields = extraction.fields as Record<string, unknown>;
    const extractedValue = fields[extractionKey];
    if (extractedValue == null || extractedValue === '') continue;

    candidates.push({
      extractedValue: String(extractedValue),
      extractionFieldKey: extractionKey,
      documentId: extraction.documentId,
      category,
      filename: idToFilename?.get(extraction.documentId),
      productId,
      tenantName,
      policyId,
      branchId,
      brokerageFirmId,
      entityType,
    });
  }

  return candidates;
}

function useAutoFillFieldPolicy(fieldKey: string): AutoFillFieldData[] {
  const params = useParams<{ policyId?: string; branchId?: string }>();
  const policyId = params.policyId;
  const branchId = params.branchId;

  const { data: policy } = useQuery({
    ...policyControllerGetPolicyQueryOptions(policyId!, { branchId }),
    enabled: !!policyId,
  });

  const productId = policy?.productId;

  const { data: productConfig } = useQuery({
    ...configControllerGetPublicProductConfigQueryOptions(productId!),
    enabled: !!productId,
  });

  const aiConfig = productConfig?.ai as Record<string, unknown> | undefined;
  const enabled = !!(aiConfig?.autofill as { enabled?: boolean } | undefined)?.enabled;

  // Public product config doesn't expose `tenantName`; the public tenant config
  // does. Fetching it here piggybacks on TanStack's cache (other folio modules
  // already prime it) so the extra request is free in practice — and it lets
  // us emit a `tenantName` on the prefill_field_outcome event without a backend
  // OpenAPI change.
  const { data: tenantConfigForProduct } = useQuery({
    ...configControllerGetPublicTenantConfigQueryOptions(),
    enabled: enabled && !!policyId,
  });

  const { data: extractionsData } = useQuery({
    ...policyDocumentExtractionControllerGetPolicyExtractionsQueryOptions(policyId!, { branchId }),
    enabled: enabled && !!policyId,
  });

  const { data: documentsData } = useQuery({
    ...policyControllerListPolicyDocumentsQueryOptions(policyId!, { branchId }),
    enabled: enabled && !!policyId,
  });

  const idToFilename = useMemo(() => {
    const map = new Map<string, string>();
    for (const doc of documentsData?.data ?? []) {
      if (doc.id && doc.name) map.set(doc.id, doc.name);
    }
    return map;
  }, [documentsData]);

  return useMemo(() => {
    if (!enabled || !extractionsData?.extractions || !productConfig) return [];

    // Search per entity so the returned `entityType` reflects where the field lives —
    // first customer, then asset, then policy (matches backend findMatchedFields order).
    const tenantName = tenantConfigForProduct?.tenantName;
    const groups: { fields: ConfigField[]; entityType: AutoFillEntityType }[] = [
      { fields: productConfig.customerFields ?? [], entityType: 'customer' },
      { fields: productConfig.assetFields ?? [], entityType: 'asset' },
      { fields: productConfig.policyFields ?? [], entityType: 'policy' },
    ];
    for (const { fields, entityType } of groups) {
      const matches = findExtractedCandidates(
        fields,
        extractionsData.extractions,
        fieldKey,
        entityType,
        idToFilename,
        productId,
        tenantName,
        policyId,
        branchId,
      );
      if (matches.length > 0) return matches;
    }
    return [];
  }, [
    enabled,
    extractionsData,
    productConfig,
    fieldKey,
    idToFilename,
    productId,
    tenantConfigForProduct,
    policyId,
    branchId,
  ]);
}

function useAutoFillFieldBrokerageFirm(fieldKey: string): AutoFillFieldData[] {
  const params = useParams<{ policyId?: string; brokerageFirmId?: string }>();
  const brokerageFirmId = params.brokerageFirmId;

  const { data: tenantConfig } = useQuery({
    ...configControllerGetPublicTenantConfigQueryOptions(),
    enabled: !!brokerageFirmId,
  });

  const aiConfig = tenantConfig?.ai as Record<string, unknown> | undefined;
  // The policy funnel also carries a brokerageFirmId, but `useAutoFillField`
  // discards this hook's result when a policyId is present — so stay inert there.
  const enabled =
    !params.policyId && !!(aiConfig?.autofill as { enabled?: boolean } | undefined)?.enabled;

  const { data: extractionsData } = useQuery({
    ...brokerageFirmDocumentExtractionControllerGetBrokerageFirmExtractionsQueryOptions(
      brokerageFirmId!,
    ),
    enabled: enabled && !!brokerageFirmId,
  });

  const { data: documentsData } = useQuery({
    ...brokerageFirmControllerListDocumentsQueryOptions(brokerageFirmId!),
    enabled: enabled && !!brokerageFirmId,
  });

  const idToFilename = useMemo(() => {
    const map = new Map<string, string>();
    for (const doc of documentsData?.data ?? []) {
      if (doc.id && doc.name) map.set(doc.id, doc.name);
    }
    return map;
  }, [documentsData]);

  return useMemo(() => {
    if (!enabled || !extractionsData?.extractions || !tenantConfig) return [];

    return findExtractedCandidates(
      tenantConfig.brokerageFirmCustomFields ?? [],
      extractionsData.extractions,
      fieldKey,
      'brokerage_firm',
      idToFilename,
      undefined,
      tenantConfig.tenantName,
      undefined,
      undefined,
      brokerageFirmId,
    );
  }, [enabled, extractionsData, tenantConfig, fieldKey, idToFilename, brokerageFirmId]);
}

export function useAutoFillField(fieldKey: string): AutoFillFieldData[] {
  const params = useParams<{ policyId?: string; brokerageFirmId?: string }>();
  const policyResult = useAutoFillFieldPolicy(fieldKey);
  const brokerageFirmResult = useAutoFillFieldBrokerageFirm(fieldKey);

  return params.policyId ? policyResult : brokerageFirmResult;
}
